import { apiRoutes } from '@socialfy/api-contract';
import { useQueryClient } from '@tanstack/react-query';
import { errorMessage } from '@/api/error-text';
import { useApiMutation, useApiQuery } from '@/api/query';
import { useSession } from '@/api/session';
import { de } from '@/i18n/de';
import { reminderAction, reminderText } from '@/i18n/messages';
import { Button, buttonStyles } from '@/shell/ui/button';
import { InternalLink } from '@/shell/ui/internal-link';
import { StatusBadge, type StatusTone } from '@/shell/ui/status-badge';
import { useToast } from '@/shell/ui/toast';

const SEVERITY: Record<'critical' | 'warning' | 'info', { tone: StatusTone; label: string }> = {
  critical: { tone: 'critical', label: de.ui.statusCritical },
  warning: { tone: 'serious', label: de.ui.statusSerious },
  info: { tone: 'notice', label: de.ui.statusNotice },
};

/**
 * Globales Banner mit der höchsten offenen Erinnerung ([A] 5.2). Der Server sortiert nach Stufe (kritisch vor
 * Warnung vor Hinweis), darum genügt die erste Seite mit einem Eintrag. Die Texte stammen aus dem Wörterbuch;
 * ein Fehler beim Laden stört die Seite nicht (dann gibt es einfach kein Banner).
 */
export function ReminderBanner() {
  const session = useSession();
  const queryClient = useQueryClient();
  const query = useApiQuery(
    apiRoutes.core.listReminders,
    { query: { limit: 1 } },
    { enabled: session.status === 'ready' },
  );
  const dismiss = useApiMutation(apiRoutes.core.dismissReminder);
  const { toast } = useToast();
  const reminder = query.data?.items[0];
  if (reminder === undefined) {
    return query.error === null ? null : (
      <section
        aria-label={de.ui.notifications}
        className="border-b border-line bg-surface-sunken px-4 py-3"
      >
        <div className="mx-auto flex max-w-[1200px] flex-wrap items-center gap-x-4 gap-y-2 lg:px-6">
          <p className="min-w-0 flex-1 text-body text-ink">{de.reminder.loadFailed}</p>
          <Button variant="quiet" onClick={() => void query.refetch()}>
            {de.states.retry}
          </Button>
        </div>
      </section>
    );
  }
  const { tone, label } = SEVERITY[reminder.severity];

  return (
    <section
      aria-label={de.ui.notifications}
      className="border-b border-line bg-surface-sunken px-4 py-3"
    >
      <div className="mx-auto flex max-w-[1200px] flex-wrap items-center gap-x-4 gap-y-2 lg:px-6">
        <StatusBadge tone={tone} label={label} />
        <p className="min-w-0 basis-full text-body text-ink sm:flex-1 sm:basis-auto">
          {reminderText(reminder.kind, reminder.params)}
        </p>
        {reminder.action === null ? null : (
          <InternalLink
            href={reminder.action.route}
            className={buttonStyles({ variant: 'primary' })}
          >
            {reminderAction(reminder.kind)}
          </InternalLink>
        )}
        <Button
          variant="quiet"
          disabled={dismiss.isPending}
          onClick={() =>
            dismiss.mutate(
              { params: { reminderId: reminder.id } },
              {
                onSuccess: () =>
                  void queryClient.invalidateQueries({ queryKey: ['core.listReminders'] }),
                onError: (error) => toast({ message: errorMessage(error) }),
              },
            )
          }
        >
          {de.reminder.dismiss}
        </Button>
      </div>
    </section>
  );
}
